import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import Footer from "../Footer";
import LanguageContext from "../../utils/Contexts/LanguageContext";
import _ from "lodash";
import UserContext from "../../utils/Contexts/UserContext";
import axiosInstance from "../../utils/Requests/axios";
import toast from "react-hot-toast";
import TablePagination from "../Common/Pagination";
import ContractTable from "./ContractTable";
import ContractsFilterModal from "../../modals/ContractsFilterModal";
import FilterIcon from "../svg/FilterIcon";
import ThemeContext from "../../utils/Contexts/ThemeContext";

const YourContracts = () => {
  const { t } = useContext(LanguageContext);
  const { selectedTheme } = useContext(ThemeContext);
  const { userData } = useContext(UserContext);
  const [pageCount, setPageCount] = useState(15);
  const [activePage, setActivePage] = useState(1);
  const [columns, setColumns] = useState({
    contract: true,
    created: true,
    placement: true,
    link_type: true,
    money_back: true,
    link_replacement: true,
    status: true,
    price: true,
    links_check: true,
  });
  const allStatuses = [
    { value: null, name: t("All Statuses") },
    { value: 1, name: t("Waiting Acceptance") },
    { value: 4, name: t("In Progress") },
  ];
  const anyAffiliation = [
    { value: null, name: t("All Check") },
    { value: 1, name: t("Successful") },
    { value: 0, name: t("Failed") },
  ];

  const allTypes = [
    { value: null, name: t("All Types") },
    { value: 1, name: t("Incoming") },
    { value: 0, name: t("Outgoing") },
  ];

  const [activeStatusTab, setActiveStatusTab] = useState(null);
  const [activeAffiliationTab, setActiveAffiliationTab] = useState(null);
  const [activeTypesTab, setActiveTypesTab] = useState(null);

  const handleStatusTabClick = (relValue) => {
    setActiveStatusTab(relValue);
    updateFilter("status", relValue);
  };

  const handleCheckTabClick = (relValue) => {
    setActiveAffiliationTab(relValue);
    updateFilter("links_check", relValue);
  };

  const handleTypesTabClick = (relValue) => {
    setActiveTypesTab(relValue);
    updateFilter("incoming", relValue);
  };

  const handleToggle = (column) => {
    setColumns((prevColumns) => ({
      ...prevColumns,
      [column]: !prevColumns[column],
    }));
  };

  const [columnSorts, setColumnSorts] = useState({
    0: null,
    1: "desc",
    2: null,
    3: null,
    4: null,
    5: null,
    6: null,
    7: null,
    8: null,
    9: null,
  });
  const [tableData, setTableData] = useState(null);
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [formData, setFormData] = useState({
    draw: 1,
    columns: [
      { data: "code" },
      { data: "created" },
      { data: "type" },
      { data: "moneyback_policy" },
      { data: "link_replacement_policy" },
      { data: "status" },
      { data: "price" },
      { data: "links_check" },
    ],
    order: [{ column: "1", dir: "desc" }],
    start: 0,
    length: 15,
    search: {
      value: "",
      filters: [],
    },
  });

  useEffect(() => {
    const payload = { ...formData };
    const start = activePage === 1 ? 0 : (activePage - 1) * pageCount;
    if (payload.start !== start || payload.length !== pageCount) {
      payload.start = start;
      payload.length = pageCount;
      setFormData(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, pageCount]);

  const fetchContracts = () => {
    const payload = _.cloneDeep(formData);
    const url = `users/${userData?.uuid}/contracts/search`;
    axiosInstance
      .post(url, payload)
      .then((response) => {
        const data = response?.data?.data || [];
        const filtered = response?.data?.recordsFiltered || 0;
        setTableData(data);
        setRecordsFiltered(filtered);
      })
      .catch(() => {
        toast.error(t("There was an error fetching backlinks offers"));
      });
  };

  useEffect(() => {
    const payload = { ...formData };
    const order = [];
    Object.keys(columnSorts).forEach((columnIndex) => {
      if (columnSorts[columnIndex]) {
        order.push({
          column: columnIndex,
          dir: columnSorts[columnIndex],
        });
      }
    });
    if (order.length) {
      payload.order = order;
      setFormData(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSorts]);

  const previousPayloadRef = useRef(null);
  useEffect(() => {
    const payload = { ...formData };
    if (!_.isEqual(previousPayloadRef.current, payload)) {
      previousPayloadRef.current = payload;
      fetchContracts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const updateFilter = (key, value) => {
    setFormData((prevData) => {
      const updatedFilters =
        value === null
          ? prevData.search.filters.filter((filter) => filter.key !== key)
          : [
              ...prevData.search.filters.filter((filter) => filter.key !== key),
              { key: key, value: value },
            ];

      return {
        ...prevData,
        length: 15,
        search: {
          ...prevData.search,
          filters: updatedFilters,
        },
      };
    });
  };

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      search: {
        ...prevData.search,
        value: searchTerm,
      },
    }));
  }, [searchTerm]);

  // eslint-disable-next-line
  const debouncedSearch = useCallback(
    _.debounce((searchTerm) => {
      setActivePage(1);
      setSearchTerm(searchTerm);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSearch(search);
    return () => {
      debouncedSearch.cancel();
    };
  }, [search, debouncedSearch]);

  const [pendingFilters, setPendingFilters] = useState([]);
  const updatePendingFilter = (key, value) => {
    setPendingFilters((prevFilters) => {
      const updatedFilters = prevFilters.map((filter) =>
        filter.key === key ? { key, value } : filter
      );

      const finalFilters = updatedFilters.some((filter) => filter.key === key)
        ? updatedFilters
        : [...updatedFilters, { key, value }];

      return finalFilters;
    });
  };

  return (
    <main id="content" role="main" className="main">
      <div className="content container-fluid">
        <div className="page-header" style={{ paddingBottom: "1em" }}>
          <div className="row align-items-center mb-3">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb breadcrumb-no-gutter">
                <li className="breadcrumb-item">
                  {/* eslint-disable-next-line */}
                  <a className="breadcrumb-link" href="javascript:;">
                    <span
                      style={{
                        color: "#919293",
                        fontSize: "18px",
                        fontWeight: "600",
                      }}
                    >
                      {t("Contracts")}
                    </span>
                  </a>
                </li>
              </ol>
            </nav>

            <div className="col-md d-md-flex justify-content-between align-items-center mb-sm-2 mb-2 mb-md-0">
              <h1
                className="page-header-title"
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                }}
              >
                {t("Your Contracts")}
              </h1>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header contract_header_container">
            <div
              className="mb-2 mb-sm-0 me-auto"
              style={{ width: "306px", height: "47px" }}
            >
              <div className="input-group input-group-merge input-group-flush border rounded-2">
                <div className="input-group-prepend input-group-text px-2">
                  <i className="bi-search"></i>
                </div>
                <input
                  id="datatableSearch"
                  type="search"
                  className="form-control custom-placeholder contract-search-input pe-2"
                  placeholder={t("Search Contract")}
                  aria-label="Search Contract"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <ul
              className="nav nav-segment  d-xl-flex d-none"
              role="tablist"
              style={{ borderRadius: "10px", width: "408px", height: "48px" }}
            >
              {allStatuses.map((status) => {
                return (
                  <li className="nav-item contract-nav" key={status.value}>
                    <a
                      className={`js-loading-state-invoker nav-link ${
                        activeStatusTab === status.value ? "active" : ""
                      }`}
                      id="nav-one-eg1-tab"
                      style={{
                        color:
                          activeStatusTab === status.value
                            ? selectedTheme === "dark"
                              ? "#FFFFFF"
                              : "#000000"
                            : "#919293",
                        borderRadius:
                          activeStatusTab === status.value
                            ? "5px !important"
                            : "0px",
                      }}
                      data-bs-toggle="pill"
                      href="#nav-one-eg1"
                      role="tab"
                      aria-controls="nav-one-eg1"
                      aria-selected={activeStatusTab === status.value}
                      onClick={(e) => {
                        e.preventDefault();
                        handleStatusTabClick(status.value);
                      }}
                      data-hs-loading-state-options='{
                        "targetEl": "#nav-one-eg1",
                        "removeLoaderDelay": "300"
                      }'
                    >
                      {status.name}
                    </a>
                  </li>
                );
              })}
            </ul>
            <ul
              className="nav nav-segment d-xl-flex d-none"
              role="tablist"
              style={{ borderRadius: "10px", width: "284px", height: "48px" }}
            >
              {anyAffiliation.map((aff) => {
                return (
                  <li className="nav-item contract-nav" key={aff.value}>
                    <a
                      className={`js-loading-state-invoker nav-link ${
                        activeAffiliationTab === aff.value ? "active" : ""
                      }`}
                      style={{
                        color:
                          activeAffiliationTab === aff.value
                            ? selectedTheme === "dark"
                              ? "#FFFFFF"
                              : "#000000"
                            : "#919293",
                      }}
                      id="nav-one-eg1-tab"
                      data-bs-toggle="pill"
                      href="#nav-one-eg1"
                      role="tab"
                      aria-controls="nav-one-eg1"
                      aria-selected={activeAffiliationTab === aff.value}
                      onClick={(e) => {
                        e.preventDefault();
                        handleCheckTabClick(aff.value);
                      }}
                      data-hs-loading-state-options='{
                        "targetEl": "#nav-one-eg1",
                        "removeLoaderDelay": "300"
                      }'
                    >
                      {aff.name}
                    </a>
                  </li>
                );
              })}
            </ul>
            <ul
              className="nav nav-segment d-xl-flex d-none"
              role="tablist"
              style={{ borderRadius: "10px", width: "302px", height: "48px" }}
            >
              {allTypes.map((type) => {
                return (
                  <li className="nav-item contract-nav" key={type.value}>
                    <a
                      className={`js-loading-state-invoker nav-link ${
                        activeTypesTab === type.value ? "active" : ""
                      }`}
                      style={{
                        color:
                          activeTypesTab === type.value
                            ? selectedTheme === "dark"
                              ? "#FFFFFF"
                              : "#000000"
                            : "#919293",
                      }}
                      id="nav-one-eg1-tab"
                      data-bs-toggle="pill"
                      href="#nav-one-eg1"
                      role="tab"
                      aria-controls="nav-one-eg1"
                      aria-selected={activeTypesTab === type.value}
                      onClick={(e) => {
                        e.preventDefault();
                        handleTypesTabClick(type.value);
                      }}
                      data-hs-loading-state-options='{
                        "targetEl": "#nav-one-eg1",
                        "removeLoaderDelay": "300"
                      }'
                    >
                      {type.name}
                    </a>
                  </li>
                );
              })}
            </ul>
            <div className="d-grid d-flex justify-content-end align-items-center gap-2">
              <div className="d-xl-none d-block ">
                <div className="btn-group dropupend ms-auto">
                  <button
                    className="btn btn-white text-nowrap"
                    type="button"
                    data-bs-toggle="offcanvas"
                    data-bs-target="#offcanvasContractsFilter"
                    aria-controls="offcanvasContractsFilter"
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                  >
                    <FilterIcon />{" "}
                    <span className="ps-1 align-middle">
                      {t("Filters")}
                      {pendingFilters?.length > 0 &&
                        `(${pendingFilters?.length})`}
                    </span>{" "}
                  </button>
                </div>
              </div>
              <div
                className="dropdown"
                style={{ height: "47px !important", width: "133px !important" }}
              >
                <button
                  type="button"
                  className="btn btn-white  text-nowrap text-secondary column-button-contract"
                  id="showHideDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  data-bs-auto-close="outside"
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    width: "133px !important",
                    height: "47px !important",
                  }}
                >
                  <i className="bi bi-layout-three-columns me-1 text-secondary"></i>{" "}
                  {t("Columns")}
                </button>

                <div
                  className="dropdown-menu dropdown-menu-end dropdown-card"
                  aria-labelledby="showHideDropdown"
                  style={{ width: "15rem" }}
                >
                  <div className="card card-sm">
                    <div className="card-body">
                      <div className="d-grid gap-3">
                        {Object.keys(columns).map((key) => (
                          <label
                            key={key}
                            className="row form-check form-switch"
                            htmlFor={`toggleColumn_${key}`}
                          >
                            <span className="col-8 col-sm-9 ms-0">
                              <span className="me-2">
                                {t(_.startCase(key))}
                              </span>
                            </span>
                            <span className="col-4 col-sm-3 text-end">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`toggleColumn_${key}`}
                                checked={columns[key]}
                                onChange={() => handleToggle(key)}
                              />
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ContractTable
            data={tableData}
            setData={setTableData}
            columnsVisibility={columns}
            setColumnSorts={setColumnSorts}
            columnSorts={columnSorts}
          />

          <TablePagination
            activePage={activePage}
            setActivePage={setActivePage}
            recordsFiltered={recordsFiltered}
            setPageCount={setPageCount}
            pageCount={pageCount}
          />
        </div>
      </div>
      <ContractsFilterModal
        allStatuses={allStatuses}
        anyAffiliation={anyAffiliation}
        allTypes={allTypes}
        activeStatusTab={activeStatusTab}
        setActiveStatusTab={setActiveStatusTab}
        activeAffiliationTab={activeAffiliationTab}
        setActiveAffiliationTab={setActiveAffiliationTab}
        setFormData={setFormData}
        activeTypesTab={activeTypesTab}
        setActiveTypesTab={setActiveTypesTab}
        updatePendingFilter={updatePendingFilter}
        pendingFilters={pendingFilters}
      />
      <Footer />
    </main>
  );
};

export default YourContracts;
